<template>
  <div id="wrapper" class="min-h-screen text-[var(--text)]">
    <a class="skip-link" href="#main">Skip to content</a>

    <header class="site-header">
      <div class="mx-auto flex h-[var(--nav-height)] max-w-6xl items-center justify-between gap-4 px-4">
        <a href="/" class="flex items-center gap-2.5" aria-label="Balcony Games home">
          <img
            src="@/assets/images/logo.png"
            alt=""
            width="36"
            height="36"
            class="h-9 w-9 rounded-lg object-cover ring-1 ring-white/10"
          />
          <span class="font-display text-[0.95rem] font-semibold tracking-tight">Balcony Games</span>
        </a>

        <nav class="hidden items-center gap-1 text-sm text-[var(--text-muted)] md:flex" aria-label="Primary">
          <a class="nav-link" href="#games">Games</a>
          <a class="nav-link" href="#extensions">Extensions</a>
          <a class="nav-link" href="#apps">Apps</a>
          <a class="nav-link" href="#about">About</a>
          <a class="nav-link nav-cta" href="#contact">Contact</a>
        </nav>

        <button
          type="button"
          class="menu-btn md:hidden"
          :aria-expanded="menuOpen"
          aria-controls="mobile-nav"
          @click="menuOpen = !menuOpen"
        >
          <span class="sr-only">{{ menuOpen ? 'Close menu' : 'Open menu' }}</span>
          <span aria-hidden="true">{{ menuOpen ? '✕' : '☰' }}</span>
        </button>
      </div>

      <nav
        v-show="menuOpen"
        id="mobile-nav"
        class="border-t border-[var(--surface-border)] bg-[var(--bg-elevated)] px-4 py-3 md:hidden"
        aria-label="Mobile"
      >
        <div class="flex flex-col gap-1 text-sm">
          <a class="mobile-link" href="#games" @click="menuOpen = false">Games</a>
          <a class="mobile-link" href="#extensions" @click="menuOpen = false">Extensions</a>
          <a class="mobile-link" href="#apps" @click="menuOpen = false">Apps</a>
          <a class="mobile-link" href="#about" @click="menuOpen = false">About</a>
          <a class="mobile-link" href="#contact" @click="menuOpen = false">Contact</a>
        </div>
      </nav>
    </header>

    <main id="main">
      <HomeView />
    </main>

    <footer class="border-t border-[var(--surface-border)] bg-[var(--bg-elevated)]/60">
      <div
        class="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <p class="font-display font-semibold">Balcony Games</p>
          <p class="mt-1 max-w-sm text-sm text-[var(--text-muted)]">
            Indie games, language tools, and everyday apps — built with care.
          </p>
        </div>
        <div class="flex flex-wrap gap-x-5 gap-y-2 text-sm text-[var(--text-muted)]">
          <a class="hover:text-[var(--accent)]" href="#games">Games</a>
          <a class="hover:text-[var(--accent)]" href="#apps">Apps</a>
          <a class="hover:text-[var(--accent)]" href="#contact">Contact</a>
          <a class="hover:text-[var(--accent)]" href="https://discord.gg/d6vJ4ERcsd" target="_blank" rel="noopener noreferrer"
            >Discord</a
          >
          <a class="hover:text-[var(--accent)]" href="/privacy.html">Privacy</a>
          <a class="hover:text-[var(--accent)]" href="/terms.html">Terms</a>
        </div>
      </div>
      <div class="border-t border-[var(--surface-border)] py-4 text-center text-xs text-[var(--text-muted)]">
        &copy; {{ year }} Balcony Games. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import HomeView from './views/HomeView.vue'

const year = new Date().getFullYear()
const menuOpen = ref(false)
</script>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: var(--z-nav);
  border-bottom: 1px solid var(--surface-border);
  background: color-mix(in srgb, var(--bg) 78%, transparent);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.nav-link {
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  transition:
    color 0.15s ease,
    background 0.15s ease;
}

.nav-link:hover {
  color: var(--text);
  background: rgba(255, 255, 255, 0.04);
}

.nav-cta {
  margin-left: 0.25rem;
  border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent);
  color: var(--accent);
  background: var(--accent-soft);
}

.nav-cta:hover {
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 22%, transparent);
}

.menu-btn {
  display: inline-flex;
  height: 2.25rem;
  width: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  border: 1px solid var(--surface-border);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text);
  cursor: pointer;
}

.mobile-link {
  display: block;
  padding: 0.65rem 0.5rem;
  border-radius: 0.5rem;
  color: var(--text-muted);
}

.mobile-link:hover {
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
